/**
 * useTcaSessionBridge — Phase 12: TCA Session Bridge (Windows → Browser)
 *
 * Detects whether the Total Cloud Directory agent (TCA) is running on the current
 * device by probing the bridge health endpoint at http://127.0.0.1:9797/health.
 * When TCA is detected, subscribes to the SSE stream at
 * http://127.0.0.1:9797/api/session-events to receive OS-level session
 * events (lock/unlock/activity) and bridges them to the portal session:
 *
 *   os_locked    → portalSessionManager.lockPortal() (if policy allows)
 *   os_unlocked  → no action (user unlocks portal manually with credentials)
 *   os_activity  → reset inactivity timer (if policy allows)
 *
 * Group policy gates (fetched from user security_policy):
 *   portal_session_lock_on_os_lock      (default: true)
 *   portal_session_stay_alive_on_os_activity (default: true)
 *
 * The hook is gated on `enabled` so it only runs for authenticated, monitored
 * sessions. On non-Windows platforms or when TCA is not detected, the hook
 * is a no-op.
 *
 * SSE reconnect: if the SSE connection drops, the hook retries after a
 * backoff delay (1s → 2s → 5s → 10s, capped at 10s).
 */

'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { isTauriDesktop } from '@/lib/desktop/desktopUtils';
import { Capacitor } from '@capacitor/core';

const BRIDGE_BASE = 'http://127.0.0.1:9797';
const BRIDGE_HEALTH_URL = `${BRIDGE_BASE}/health`;
const SSE_URL = `${BRIDGE_BASE}/api/session-events`;
// Health-check backoff when the bridge port is closed. A failed probe logs
// an unavoidable ERR_CONNECTION_REFUSED console error (browsers log network
// failures regardless of try/catch), so we retry a few times early — the
// agent may start shortly after login — then drop to every 10 minutes.
const HEALTH_CHECK_BACKOFF_MS = [60_000, 120_000, 300_000, 600_000];
// When the agent WAS connected earlier this page-load, an offline bridge is
// almost certainly an agent restart/upgrade — a transient state that ends on
// its own. Probe faster and never give up permanently so the portal
// redetects the agent without a page reload. Each probe of a dead port still
// logs one ERR_CONNECTION_REFUSED, so this stays slower than SSE retries.
const OFFLINE_PROBE_BACKOFF_MS = [15_000, 30_000, 60_000];
const SSE_RECONNECT_DELAYS = [1000, 2000, 5000, 10000]; // backoff steps
const BRIDGE_UNREACHABLE_ERROR = 'TCA bridge unreachable — agent not installed or not running';
// Give up after this many consecutive failed probes and remember the negative
// result — every probe of a closed port logs an ERR_CONNECTION_REFUSED
// console error that JS cannot suppress. The mark expires so a page loaded
// mid-agent-update eventually re-probes instead of staying blind forever.
const MAX_PROBE_FAILURES = 3;
const BRIDGE_UNAVAILABLE_TTL_MS = 10 * 60_000;
const MAX_SSE_RECONNECT_ATTEMPTS = 5;
const TCA_UNAVAILABLE_KEY = 'ta_tca_bridge_unavailable';

function isBridgeMarkedUnavailable(): boolean {
  try {
    const ts = Number(sessionStorage.getItem(TCA_UNAVAILABLE_KEY));
    if (!ts || Date.now() - ts > BRIDGE_UNAVAILABLE_TTL_MS) {
      sessionStorage.removeItem(TCA_UNAVAILABLE_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function markBridgeUnavailable(): void {
  try {
    sessionStorage.setItem(TCA_UNAVAILABLE_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — probing still stops via MAX_PROBE_FAILURES
  }
}

function clearBridgeUnavailable(): void {
  try {
    sessionStorage.removeItem(TCA_UNAVAILABLE_KEY);
  } catch {
    // Non-critical
  }
}

export interface TcaSessionBridgeState {
  /** Whether TCA bridge was detected on this device. */
  tcaDetected: boolean;
  /** Whether the SSE stream is currently connected. */
  sseConnected: boolean;
  /** Current OS lock state (from the last received event). */
  osLocked: boolean | null;
  /** Last error message, if any. */
  error: string | null;
}

export interface UseTcaSessionBridgeOptions {
  /** Whether session monitoring is active (gates the entire hook). */
  enabled: boolean;
  /** Whether to lock the portal when OS is locked. Default: true. */
  lockOnOsLock?: boolean;
  /** Whether to reset inactivity timer on OS activity. Default: true. */
  stayAliveOnOsActivity?: boolean;
}

/**
 * Probe the TCA bridge health endpoint to detect if the agent is running.
 *
 * Uses no-cors mode to avoid browser-level CORS console errors on every poll
 * (the browser logs CORS violations regardless of JS try/catch). With no-cors,
 * a successful connection returns an opaque response — we can't read the body,
 * but we know something is listening on the port.
 *
 * CORS verification is done separately in checkBridgeAndConnect before
 * attempting the SSE connection (which requires CORS).
 */
async function probeTcaBridge(signal: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(BRIDGE_HEALTH_URL, {
      method: 'GET',
      mode: 'no-cors',
      credentials: 'omit',
      signal,
    });
    // With no-cors, a successful connection returns an opaque response
    // (type 'opaque', status 0). If we get here without throwing, something
    // is listening on the port.
    return response.type === 'opaque' || response.ok;
  } catch {
    return false;
  }
}

/**
 * Verify that CORS works with the bridge by doing a normal CORS fetch.
 * This is needed because the SSE connection (EventSource) requires CORS.
 * Returns true if CORS works and the bridge is healthy.
 */
async function verifyBridgeCors(signal: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(BRIDGE_HEALTH_URL, {
      method: 'GET',
      credentials: 'omit',
      signal,
    });
    if (!response.ok) return false;
    const data = await response.json().catch(() => null) as { status?: string } | null;
    return data?.status === 'ok';
  } catch {
    return false;
  }
}

export function useTcaSessionBridge({
  enabled,
  lockOnOsLock = true,
  stayAliveOnOsActivity = true,
}: UseTcaSessionBridgeOptions): TcaSessionBridgeState {
  const [state, setState] = useState<TcaSessionBridgeState>({
    tcaDetected: false,
    sseConnected: false,
    osLocked: null,
    error: null,
  });

  // Refs to avoid stale closures in the SSE event handler
  const lockOnOsLockRef = useRef(lockOnOsLock);
  const stayAliveRef = useRef(stayAliveOnOsActivity);
  lockOnOsLockRef.current = lockOnOsLock;
  stayAliveRef.current = stayAliveOnOsActivity;

  // Track active EventSource and reconnect timer
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const healthTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const mountedRef = useRef(true);
  // Whether the SSE stream ever opened this page-load. A previously-connected
  // agent going offline is expected (restart/upgrade), so the health loop
  // keeps probing at a slower cadence instead of marking the bridge
  // permanently unavailable.
  const wasConnectedRef = useRef(false);
  // Track whether a CORS fetch to the bridge has failed. When true, subsequent
  // health checks skip the CORS verification (and SSE connection) to avoid
  // spamming the console with CORS errors on every 60s poll. Reset on page reload.
  const corsFetchFailedRef = useRef(false);

  /**
   * Handle an incoming SSE session event from the TCA bridge.
   */
  const handleSessionEvent = useCallback(async (eventData: unknown) => {
    if (!eventData || typeof eventData !== 'object') return;
    const ev = eventData as { type?: string; timestamp?: number };
    const type = ev.type;
    if (!type) return;

    // Update osLocked state for all relevant event types
    if (type === 'os_locked') {
      setState(prev => ({ ...prev, osLocked: true }));
      if (lockOnOsLockRef.current) {
        try {
          const { portalSessionManager } = await import('@/lib/portalSessionManager');
          portalSessionManager.lockPortal?.();
        } catch (err) {
          console.warn('[useTcaSessionBridge] lockPortal failed:', err);
        }
      }
    } else if (type === 'os_unlocked') {
      setState(prev => ({ ...prev, osLocked: false }));
      // Don't auto-unlock the portal — the user should unlock with their
      // credentials, same as the normal portal lock flow.
    } else if (type === 'os_activity') {
      if (stayAliveRef.current) {
        try {
          const { portalSessionManager } = await import('@/lib/portalSessionManager');
          // Resets the inactivity timers and forwards a WS `activity` message
          // to the server enforcer (via PORTAL_EXTERNAL_ACTIVITY_EVENT →
          // useSessionManager). forceHeartbeat would only revalidate the
          // session — it does not reset inactivity.
          portalSessionManager.recordExternalActivity?.();
        } catch {
          // Non-critical
        }
      }
    }
  }, []);

  /**
   * Connect to the TCA bridge SSE stream.
   */
  const connectSSE = useCallback(() => {
    if (!mountedRef.current) return;

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    const es = new EventSource(SSE_URL, { withCredentials: false });
    eventSourceRef.current = es;

    es.onopen = () => {
      if (!mountedRef.current) return;
      reconnectAttemptRef.current = 0;
      wasConnectedRef.current = true;
      setState(prev => ({ ...prev, sseConnected: true, error: null }));
    };

    // "hello" event carries the initial state
    es.addEventListener('hello', (e: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const data = JSON.parse(e.data) as { locked?: boolean };
        setState(prev => ({ ...prev, osLocked: Boolean(data.locked) }));
      } catch {
        // Non-critical
      }
    });

    // Default message events carry session events
    es.onmessage = (e: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const data = JSON.parse(e.data);
        handleSessionEvent(data);
      } catch {
        // Non-critical
      }
    };

    es.onerror = () => {
      if (!mountedRef.current) return;
      setState(prev => ({ ...prev, sseConnected: false, error: 'SSE connection lost' }));
      es.close();
      eventSourceRef.current = null;

      // Give up after a few attempts — EventSource failures log console errors
      // too. The health-check loop will redetect the agent and reconnect SSE.
      if (reconnectAttemptRef.current >= MAX_SSE_RECONNECT_ATTEMPTS) {
        return;
      }
      const attempt = reconnectAttemptRef.current;
      const delay = SSE_RECONNECT_DELAYS[Math.min(attempt, SSE_RECONNECT_DELAYS.length - 1)];
      reconnectAttemptRef.current = attempt + 1;

      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current) {
          connectSSE();
        }
      }, delay);
    };
  }, [handleSessionEvent]);

  /**
   * Check TCA bridge health and connect/disconnect SSE accordingly.
   *
   * Two-phase detection:
   *   1. no-cors probe (probeTcaBridge) — detects if anything is listening
   *      without triggering browser-level CORS console errors.
   *   2. CORS verification (verifyBridgeCors) — confirms the agent supports
   *      CORS (required for EventSource SSE) and reads the health status.
   *      If CORS fails (old agent without CORS middleware), the failure is
   *      cached so we don't retry (and re-log) on every 60s poll.
   */
  const checkBridgeAndConnect = useCallback(async () => {
    if (!mountedRef.current || !enabled || isBridgeMarkedUnavailable()) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
      // Phase 1: no-cors probe (no CORS console errors)
      const detected = await probeTcaBridge(controller.signal);
      if (!mountedRef.current) return;

      if (!detected) {
        consecutiveFailuresRef.current += 1;
        // Bridge not reachable — disconnect SSE if it was connected and
        // surface the unreachable state via `error` so consumers can react.
        setState(prev => {
          if (prev.sseConnected) {
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            return { ...prev, tcaDetected: false, sseConnected: false, osLocked: null, error: BRIDGE_UNREACHABLE_ERROR };
          }
          return { ...prev, tcaDetected: false, error: BRIDGE_UNREACHABLE_ERROR };
        });
        return;
      }

      // Bridge port is listening — reset the failure backoff and any
      // negative marker left by a previous session.
      consecutiveFailuresRef.current = 0;
      clearBridgeUnavailable();

      // Phase 2: CORS verification — required for EventSource SSE.
      // Skip if CORS previously failed to avoid repeated console errors.
      if (corsFetchFailedRef.current) {
        setState(prev => ({ ...prev, tcaDetected: false }));
        return;
      }

      const corsOk = await verifyBridgeCors(controller.signal);
      if (!mountedRef.current) return;

      if (!corsOk) {
        // CORS not supported by this agent version — cache the failure
        corsFetchFailedRef.current = true;
        setState(prev => ({
          ...prev,
          tcaDetected: false,
          sseConnected: false,
          osLocked: null,
          error: null,
        }));
        return;
      }

      // CORS works — mark as detected and connect SSE if not already connected
      setState(prev => ({ ...prev, tcaDetected: true, error: null }));
      if (!eventSourceRef.current) {
        connectSSE();
      }
    } catch {
      consecutiveFailuresRef.current += 1;
      // Bridge not reachable — this is normal on non-TCA devices
      if (mountedRef.current) {
        setState(prev => {
          if (prev.tcaDetected || prev.sseConnected) {
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            return { ...prev, tcaDetected: false, sseConnected: false, osLocked: null, error: BRIDGE_UNREACHABLE_ERROR };
          }
          return { ...prev, error: BRIDGE_UNREACHABLE_ERROR };
        });
      }
    } finally {
      clearTimeout(timeoutId);
    }
  }, [enabled, connectSSE]);

  useEffect(() => {
    mountedRef.current = true;

    // Skip on mobile/Tauri — and on non-Windows browsers. The TCA bridge only
    // exists on Windows (installed via agent.nsi), so probing elsewhere just
    // produces ERR_CONNECTION_REFUSED console noise on every poll.
    const isWindows = typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent);
    if (isTauriDesktop() || Capacitor.isNativePlatform() || !isWindows) {
      return;
    }

    if (!enabled) {
      // Clean up any existing connection when disabled
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (healthTimerRef.current) {
        clearTimeout(healthTimerRef.current);
        healthTimerRef.current = null;
      }
      setState({ tcaDetected: false, sseConnected: false, osLocked: null, error: null });
      return;
    }

    // Initial check immediately, then self-scheduling re-checks that back off
    // while the bridge is absent (each failed probe logs a console error).
    // After MAX_PROBE_FAILURES consecutive misses on a never-connected
    // machine we mark the bridge unavailable and stop — non-TCA machines
    // should not be polled indefinitely. An agent that WAS connected gets the
    // faster offline cadence instead, so it is redetected after a
    // restart/upgrade without a page reload.
    const tick = async () => {
      await checkBridgeAndConnect();
      if (!mountedRef.current) return;
      if (!wasConnectedRef.current && consecutiveFailuresRef.current >= MAX_PROBE_FAILURES) {
        markBridgeUnavailable();
        return;
      }
      const backoff = wasConnectedRef.current ? OFFLINE_PROBE_BACKOFF_MS : HEALTH_CHECK_BACKOFF_MS;
      const delay = backoff[Math.min(consecutiveFailuresRef.current, backoff.length - 1)];
      healthTimerRef.current = setTimeout(() => { void tick(); }, delay);
    };

    // A previous page load in this tab already established the bridge is
    // absent — don't probe again until the mark expires. A deferred re-probe
    // is still scheduled so the portal recovers on its own when the mark was
    // set during e.g. an agent update window.
    if (isBridgeMarkedUnavailable()) {
      setState(prev => ({ ...prev, error: BRIDGE_UNREACHABLE_ERROR }));
      healthTimerRef.current = setTimeout(() => {
        clearBridgeUnavailable();
        void tick();
      }, BRIDGE_UNAVAILABLE_TTL_MS);
      return () => {
        mountedRef.current = false;
        if (healthTimerRef.current) {
          clearTimeout(healthTimerRef.current);
          healthTimerRef.current = null;
        }
      };
    }

    void tick();

    return () => {
      mountedRef.current = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (healthTimerRef.current) {
        clearTimeout(healthTimerRef.current);
        healthTimerRef.current = null;
      }
    };
  }, [enabled, checkBridgeAndConnect]);

  return state;
}
